import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../../services/alert.service';
import Datatable from '../../../../components/datatable/datatable-xl.component';
import TruncatedName from '../../../../components/forms/truncated-name.component';
import Icon from '../../../../components/forms/icon.component';

interface Props {
  rows: any[];
  isLoading?: boolean;
  canWrite?: boolean;
  /** true si el usuario en sesion es superadmin (mirror de `assertCanModifyTarget`). */
  canManageSuperAdmin?: boolean;
  onEdit?: (row: any) => void;
  onDelete?: (id: string | number) => Promise<void>;
  onResetPassword?: (row: any) => Promise<void>;
}

function resolveLocale(language: string): string {
  const lang = (language || '').toLowerCase();
  if (lang.startsWith('zh')) return 'zh-CN';
  if (lang.startsWith('en')) return 'en-US';
  return 'es-MX';
}

function formatDate(iso: string, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Espejo de `assertCanModifyTarget` del backend: solo un superadmin puede
 * editar, borrar o resetear el password de otro superadmin. El backend
 * responde 403 de todas formas; esto solo evita el viaje.
 */
const isLockedTarget = (row: any, canManageSuperAdmin: boolean): boolean =>
  !canManageSuperAdmin && Array.isArray(row?.roles) && row.roles.includes('superadmin');

const LOCKED_TITLE = 'Solo un superadmin puede modificar a otro superadmin';

const ROLE_BADGE: Record<string, string> = {
  superadmin: 'badge-light-danger text-danger',
  admin: 'badge-light-primary text-primary',
  supervisor: 'badge-light-info text-info',
  operador: 'badge-light-secondary text-gray-700'
};

export default function TableUsers({
  rows,
  isLoading = false,
  canWrite = false,
  canManageSuperAdmin = false,
  onEdit,
  onDelete,
  onResetPassword
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const roleLabels: Record<string, string> = {
    superadmin: 'Superadmin',
    admin: t('users-page.admin-profile'),
    supervisor: 'Supervisor',
    operador: t('users-page.standard-user-profile')
  };

  const handleDeleteClick = async (r: any) => {
    setOpenDropdownId(null);
    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      t('users-page.delete-user-confirm', { username: r.username || r.name || `#${r.id}` }),
      () => Promise.resolve(true),
      'Continuar'
    );

    if (!isConfirmed) return;
    if (onDelete) await onDelete(r.id);
  };

  const handleResetClick = async (r: any) => {
    setOpenDropdownId(null);
    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      t('users-page.recover-password-confirm', { username: r.username || r.name || `#${r.id}` }),
      () => Promise.resolve(true),
      'Continuar'
    );

    if (!isConfirmed) return;
    if (onResetPassword) await onResetPassword(r);
  };

  const columns = useMemo(() => {
    return [
      {
        name: t('users-page.title-column-1'),
        sortable: true,
        minWidth: '220px',
        selector: (r: any) => r.name,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <TruncatedName
              text={r.name || '—'}
              maxWidth={200}
              className="fw-bolder fs-6 text-dark"
            />
            <span className="text-muted fs-8">@{r.username}</span>
          </div>
        )
      },
      {
        name: t('users-page.modal-add-user.email-label'),
        sortable: true,
        minWidth: '220px',
        selector: (r: any) => r.email,
        cell: (r: any) => (
          <TruncatedName text={r.email || '—'} maxWidth={220} className="fs-7 text-gray-700" />
        )
      },
      {
        name: t('users-page.title-profile'),
        sortable: false,
        minWidth: '180px',
        selector: (r: any) => (r.roles || []).join(', '),
        cell: (r: any) => (
          <div className="d-flex flex-wrap gap-1">
            {(r.roles && r.roles.length > 0 ? r.roles : ['—']).map((role: string) => (
              <span
                key={role}
                className={`badge ${ROLE_BADGE[role] || 'badge-light text-gray-700'} fw-bold fs-8 text-uppercase`}
              >
                {roleLabels[role] || role}
              </span>
            ))}
          </div>
        )
      },
      {
        name: t('users-page.title-column-5'),
        sortable: true,
        center: true,
        minWidth: '100px',
        selector: (r: any) => r.enabled,
        cell: (r: any) => (
          <span
            className={`badge ${r.enabled ? 'badge-light-success text-success' : 'badge-light-danger text-danger'} fw-bold fs-8`}
          >
            {r.enabled
              ? t('users-page.rolePermissions.enabled')
              : t('users-page.rolePermissions.disabled')}
          </span>
        )
      },
      {
        name: t('users-page.title-column-4'),
        sortable: true,
        minWidth: '150px',
        selector: (r: any) => r.updatedAt,
        cell: (r: any) => (
          <div className="d-flex flex-column">
            <span className="fs-7 fw-semibold text-gray-800">
              {formatDate(r.updatedAt, locale)}
            </span>
            <span className="text-muted fs-9">{formatDate(r.createdAt, locale)}</span>
          </div>
        )
      },
      {
        name: t('general.actions', 'Acciones'),
        center: true,
        minWidth: '160px',
        omit: !canWrite,
        cell: (r: any) => {
          const isDropdownOpen = openDropdownId === r.id;
          const locked = isLockedTarget(r, canManageSuperAdmin);

          return (
            <div className="d-flex align-items-center gap-2 position-relative">
              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-primary btn-active-light-primary w-30px h-30px rounded-3"
                title={locked ? LOCKED_TITLE : t('users-page.modal-add-user.edit-user-title')}
                disabled={locked}
                onClick={() => onEdit && onEdit(r)}
              >
                <Icon icon="ki-pencil" size="fs-5" />
              </button>

              <button
                type="button"
                className="btn btn-icon btn-bg-light btn-color-danger btn-active-light-danger w-30px h-30px rounded-3"
                title={
                  locked ? LOCKED_TITLE : t('users-page.delete-user-confirm', { username: '' })
                }
                disabled={locked}
                onClick={() => handleDeleteClick(r)}
              >
                <Icon icon="ki-trash" size="fs-5" />
              </button>

              <div className="dropdown">
                <button
                  type="button"
                  className={`btn btn-icon btn-bg-light btn-color-gray-600 btn-active-light-dark w-30px h-30px rounded-3 ${isDropdownOpen ? 'show' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(isDropdownOpen ? null : r.id);
                  }}
                >
                  <Icon icon="ki-dots-horizontal" size="fs-4" />
                </button>

                {isDropdownOpen && (
                  <>
                    <div
                      className="position-fixed top-0 start-0 w-100 h-100"
                      style={{ zIndex: 99, background: 'transparent' }}
                      onClick={() => setOpenDropdownId(null)}
                    />
                    <div
                      className="dropdown-menu menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold w-200px py-3 show"
                      style={{
                        position: 'absolute',
                        inset: 'auto 0px 0px auto',
                        transform: 'translate3d(0px, 35px, 0px)',
                        zIndex: 100,
                        display: 'block'
                      }}
                    >
                      <div className="menu-item px-3">
                        <a
                          href="#reset-password"
                          className={`menu-link px-3 fs-7 ${locked ? 'text-muted disabled' : 'text-warning'}`}
                          title={locked ? LOCKED_TITLE : undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            if (locked) return;
                            handleResetClick(r);
                          }}
                        >
                          <Icon icon="ki-key" size="fs-6 me-2" />{' '}
                          {t('users-page.modal-add-user.recover-password-title')}
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }
      }
    ];
  }, [locale, openDropdownId, canWrite, canManageSuperAdmin, t]);

  return <Datatable key="users-table" columns={columns} data={rows} isLoading={isLoading} />;
}
